import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { IRUConfiguration } from '@interfaces/cmModule/ruConfiguration';

interface IColumns {
	isViewerOnly: boolean;
	openConfigurationFormModal: (configuration: IRUConfiguration, isEditMode?: boolean) => void;
	openViewConfigurationModal: (configuration: IRUConfiguration) => void;
	openViewErrorResultModal: (configuration: IRUConfiguration) => void;
}

const columnHelper = createColumnHelper<IRUConfiguration>();

export const getColumns = ({
	isViewerOnly,
	openConfigurationFormModal,
	openViewConfigurationModal,
	openViewErrorResultModal,
}: IColumns): ColumnDef<any>[] => [
	columnHelper.accessor('networkElementName', {
		header: 'Name',
		cell: ({ getValue }: CellContext<IRUConfiguration, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value}/>;
		},
	}),
	columnHelper.accessor('middlewareName', {
		header: 'Middleware',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUConfiguration, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value}/>;
		},
	}),
	columnHelper.accessor('status', {
		header: 'Status',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUConfiguration, any>) => {
			const { title, color } = getValue() || {};

			return (
				<AdaptiveTableCell
					color={color}
					text={title || 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('netconfConfiguratedStatus', {
		header: 'Configuration',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUConfiguration, any>) => {
			const { title, color } = getValue() || {};

			return (
				<AdaptiveTableCell
					color={color}
					text={title || 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('netconfApplyResults', {
		enableSorting: false,
		header: () => <>Netconf<br/>apply result</>,
		cell: ({ row: { original } }: CellContext<IRUConfiguration, any>) => {
			const {
				updateResult,
				netconfApplyResults,
			} = original || {};

			const {
				isErrorCuPlaneVlanId,
				isErrorDuMacAddress,
				isErrorRuMacAddress,
				isErrorCarriers,
			} = updateResult || {};
			const { title, color } = netconfApplyResults || {};

			const isError = isErrorCuPlaneVlanId || isErrorDuMacAddress || isErrorRuMacAddress || isErrorCarriers;

			const viewHandler = () => {
				if (isError) {
					openViewErrorResultModal(original);
				}
			};

			return (
				<AdaptiveTableCell
					actionHandler={isError ? viewHandler : undefined}
					color={color}
					text={title || '-'}
				/>
			);
		},
	}),
	columnHelper.accessor('updatedAtText', {
		header: 'updated',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUConfiguration, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value || '-'} />;
		},
	}),
	columnHelper.accessor('userName', {
		header: 'User',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUConfiguration, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value || '-'} />;
		},
	}),
	columnHelper.accessor('pushedAtText', {
		header: 'Last push attempt',
		cell: ({ getValue }: CellContext<IRUConfiguration, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value || '-'} />;
		},
	}),
	columnHelper.accessor('createdAt', {
		header: 'Actions',
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IRUConfiguration, any>) => {
			const { isFilled } = original || {};

			const viewHandler = () => {
				openViewConfigurationModal(original);
			};

			const editHandler = () => {
				openConfigurationFormModal(original, true);
			};

			const addHandler = () => {
				openConfigurationFormModal(original);
			};

			const isAdd = !isViewerOnly && !isFilled;
			const isEdit = !isViewerOnly && !!isFilled;

			return (
				<Actions
					addHandler={addHandler}
					addTooltip='Add configuration'
					editHandler={editHandler}
					editTooltip='Edit configuration'
					isAdd={isAdd}
					isEdit={isEdit}
					isView={!isAdd}
					viewHandler={viewHandler}
					viewTooltip='View configuration'
				/>
			);
		},
	}),
];
